import { useEffect, useRef, useState } from 'react'

import { ActionIcon, Collapse } from '@mantine/core'
import { Check, X } from 'lucide-react'

import { useAuth } from 'src/auth'
import { isAdminOrOps } from 'src/lib/auth/permissions'
import DNButton from 'src/components/Buttons/Button/DNButton'
import IconChevronDown from 'src/icons/IconChevronDown'
import { toast } from 'src/components/Overlays/Toast/Toast'
import IconEdit from 'src/icons/IconEdit'
import { EmployeeStatus, RoleType } from 'src/graphql/types/employees'

import AtsEmailTrackingCard from './AtsEmailTrackingCard'
import AutomatedOutreachCard from './AutomatedOutreachCard'
import type { AutomatedOutreachCardRef } from './AutomatedOutreachCard'
import VerificationRequestsCard from './VerificationRequestsCard'
import type { VerificationRequestsCardRef } from './VerificationRequestsCard'

interface Employee {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  status?: EmployeeStatus
  roleType?: RoleType
}

interface EmploymentVerificationsCardProps {
  companyData: {
    companyId: string
    incomingVoEAssignedEmployees?: Employee[] | null
    outgoingVoEAssignedEmployees?: Employee[] | null
    voeVerificationPeriodMonths?: number | null
    autoCancelOutOfWindowVoEs?: boolean | null
    atsEmailTrackingEnabled?: boolean | null
    atsInboundEmailAddress?: string | null
  }
  recruiters?: Employee[]
  initialLoading?: boolean
  initialOpened?: boolean
  /** Opened and scrolled into view, e.g. when linked to from elsewhere. */
  focused?: boolean
}

/** `/settings?tab=company&section=…` value that focuses this card. */
export const EMPLOYMENT_VERIFICATIONS_SECTION = 'employmentVerifications'

const EmploymentVerificationsCard = ({
  companyData,
  recruiters = [],
  initialLoading = false,
  initialOpened = false,
  focused = false,
}: EmploymentVerificationsCardProps) => {
  const { currentUser } = useAuth()
  const isAdmin = isAdminOrOps(currentUser)
  const isRecruiter = currentUser?.roles?.includes('RECRUITER')
  const canEdit = isAdmin || isRecruiter

  const [opened, setOpened] = useState(initialOpened || focused)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focused) {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [focused])
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  const voeRequestsRef = useRef<VerificationRequestsCardRef>(null)
  const outreachRef = useRef<AutomatedOutreachCardRef>(null)

  const handleEdit = () => {
    setEditing(true)
    if (!opened) setOpened(true)
  }

  const handleSave = async () => {
    const validationError = outreachRef.current?.validationError
    if (validationError) {
      toast(validationError, 'error')
      return
    }
    setSaving(true)
    try {
      await Promise.all([
        voeRequestsRef.current?.save(),
        outreachRef.current?.save(),
      ])
    } finally {
      setSaving(false)
    }
    setEditing(false)
  }

  const handleCancel = () => {
    voeRequestsRef.current?.cancel()
    outreachRef.current?.cancel()
    setEditing(false)
  }

  return (
    <div
      ref={rootRef}
      id={EMPLOYMENT_VERIFICATIONS_SECTION}
      className="flex w-full flex-col gap-2 rounded-lg border border-doubleNickel-gray-200 p-3"
    >
      <div
        className="flex w-full cursor-pointer flex-row items-center gap-2 text-sm font-semibold text-doubleNickel-gray-900"
        onClick={() => setOpened((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpened((prev) => !prev)
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={opened}
      >
        <ActionIcon variant="subtle" className="pointer-events-none">
          <IconChevronDown
            className={`h-4 w-4 ${
              !opened && '-rotate-90'
            } fill-none stroke-doubleNickel-gray-500 transition-all`}
          />
        </ActionIcon>
        Employment Verifications
        {canEdit && !editing && (
          <DNButton
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation()
              handleEdit()
            }}
            className="ml-auto text-doubleNickel-gray-700"
          >
            <IconEdit className="h-3.5 w-3.5 fill-none stroke-current" />
            Edit
          </DNButton>
        )}
        {canEdit && editing && (
          <div className="ml-auto flex items-center gap-2">
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleSave()
              }}
              disabled={saving}
              className="text-doubleNickel-success-500"
            >
              <Check size={14} />
              Save
            </DNButton>
            <DNButton
              variant="transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleCancel()
              }}
              disabled={saving}
              className="text-doubleNickel-error-500"
            >
              <X size={14} />
              Cancel
            </DNButton>
          </div>
        )}
      </div>
      <Collapse in={opened} transitionDuration={400}>
        <div className="flex flex-col gap-4">
          <VerificationRequestsCard
            ref={voeRequestsRef}
            companyData={{
              companyId: companyData.companyId,
              incomingVoEAssignedEmployees:
                companyData.incomingVoEAssignedEmployees,
              outgoingVoEAssignedEmployees:
                companyData.outgoingVoEAssignedEmployees,
              voeVerificationPeriodMonths:
                companyData.voeVerificationPeriodMonths,
              autoCancelOutOfWindowVoEs: companyData.autoCancelOutOfWindowVoEs,
            }}
            recruiters={recruiters}
            initialLoading={initialLoading}
            editing={editing}
          />
          <AutomatedOutreachCard
            ref={outreachRef}
            companyId={companyData.companyId}
            editing={editing}
          />
          <AtsEmailTrackingCard
            companyData={{
              companyId: companyData.companyId,
              atsEmailTrackingEnabled: companyData.atsEmailTrackingEnabled ?? false,
              atsInboundEmailAddress: companyData.atsInboundEmailAddress ?? null,
            }}
          />
        </div>
      </Collapse>
    </div>
  )
}

export default EmploymentVerificationsCard
